import { BrowserRouter, MemoryRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import EntryDetail from "@/pages/EntryDetail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Support from "@/pages/Support";
import DeleteAccount from "@/pages/DeleteAccount";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone-100">
        <div className="text-ink-500 font-serif">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function PublicOnly({ children }) {
  // Do NOT block public pages on the /auth/me network check — on iPad WKWebView
  // that credentialed cross-origin request can stall (App Review reported as
  // "blank screen on tap sign in"). Render the page immediately; only redirect
  // once we actually know the user is signed in.
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return children;
}

// Detects session_id in URL fragment (Emergent OAuth return) before normal routes
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/auth"
        element={
          <PublicOnly>
            <AuthPage />
          </PublicOnly>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route
        path="/app"
        element={
          <Protected>
            <Dashboard />
          </Protected>
        }
      />
      <Route
        path="/entry/:id"
        element={
          <Protected>
            <EntryDetail />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // On Capacitor native (iOS/Android), use MemoryRouter — it doesn't depend on
  // the URL hash or pathname, which is more reliable than HashRouter on the
  // file:// scheme used inside WKWebView. Apple has rejected Hush twice citing
  // a "blank screen on sign in" when using HashRouter; MemoryRouter eliminates
  // that whole class of file://-vs-route-resolution bugs.
  // On the web, we keep BrowserRouter for clean URLs.
  const isNative = Capacitor.isNativePlatform();
  const Router = isNative ? MemoryRouter : BrowserRouter;
  // Native users should NOT see the marketing landing page — they came from
  // the App Store, they already know what Hush is. Boot them straight to /auth
  // (which redirects to /app if they're logged in via persisted session).
  const routerProps = isNative ? { initialEntries: ["/auth"] } : {};
  return (
    <ErrorBoundary>
      <div className="App">
        <Router {...routerProps}>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </Router>
      </div>
    </ErrorBoundary>
  );
}
